import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from '../lib/api/client.js';
import { devRole, devUser } from './dev-auth.js';
import type { MeResponse } from '../lib/api/types.js';

/**
 * The signed-in account, resolved once for the whole application.
 *
 * `apps/api` is authoritative for authentication (research D-004): the browser
 * holds an HttpOnly Better Auth cookie it cannot read, so the only way to know
 * who is signed in is to ask. `GET /api/me` is that question, and a 401 is a
 * complete answer rather than an error — it means "nobody", which is a normal
 * state for a route boundary to act on.
 *
 * This provider decides nothing about routing. It reports a state; the
 * boundaries in router.tsx decide what a given state may reach (FR-027,
 * FR-061).
 *
 * `dev-auth.ts` can put a role on top of that answer while `pnpm dev` runs.
 * It changes nothing about how the question is asked — /api/me is still the
 * only source of a real account — and Vite drops it from a production build.
 */

export type AuthState =
  /** The first /api/me is still in flight. Nothing may be decided yet. */
  | { readonly status: 'loading'; readonly user: null }
  | { readonly status: 'authenticated'; readonly user: MeResponse }
  | { readonly status: 'anonymous'; readonly user: null }
  /*
   * The API could not be reached, which is NOT the same as "signed out".
   * Treating a network failure as anonymous would sign a learner out
   * every time their connection dropped, so it is kept as its own state.
   */
  | { readonly status: 'unavailable'; readonly user: null };

export interface AuthContextValue {
  readonly state: AuthState;
  /** Re-reads /api/me — after sign-in returns, and after PATCH /api/me. */
  readonly refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Gives the account a zone the first time it is missing one, and never again.
 *
 * The server cannot collect it at sign-up — the OAuth callback never sees a
 * browser — so this is the substitute, and it is the browser's own
 * `Intl` answer rather than a guess from an IP address.
 *
 * ONLY WHEN IT IS NULL. Overwriting a stored zone with whatever the current
 * machine reports would reset a learner's midnight every time they opened the
 * app from a laptop in another country, moving the streak's boundary under
 * them without their asking. Once set, only the learner changes it.
 *
 * A failure here is not a failure of sign-in: the account is perfectly usable
 * without a stored zone, every boundary simply falls back to UTC, and the next
 * load tries again.
 */
async function adoptTimeZone(user: MeResponse): Promise<MeResponse> {
  if (user.timeZone) return user;

  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!detected) return user;

  try {
    return await api.updateMe({ timeZone: detected });
  } catch {
    return user;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null });

  const load = useCallback(async () => {
    // Null unless a role was chosen from the sign-in screen, and always null
    // in a production build (dev-auth.ts).
    const dev = devRole();

    try {
      const user = await adoptTimeZone(await api.me());
      // A real session is used as it stands; the dev choice only lends it a
      // role, which is what opens the administrator screens before anyone has
      // been granted the role in earnest.
      setState({ status: 'authenticated', user: dev ? { ...user, role: dev } : user });
    } catch (error) {
      // With a role chosen, neither "nobody is signed in" nor "the server did
      // not answer" is allowed to stop the screens being opened — that is the
      // whole point of the choice. Without one, both are reported as ever.
      if (dev) {
        setState({ status: 'authenticated', user: devUser(dev) });
        return;
      }
      if (error instanceof ApiError && error.status === 401) {
        setState({ status: 'anonymous', user: null });
        return;
      }
      setState({ status: 'unavailable', user: null });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo<AuthContextValue>(() => ({ state, refresh: load }), [state, load]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Throws outside a provider rather than returning a default. A route boundary
 * silently reading "anonymous" because it was mounted in the wrong tree would
 * be an authorization bug that looks like a redirect.
 */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

/** Convenience for the common read. `null` whenever nobody is established. */
export function useCurrentUser(): MeResponse | null {
  return useAuth().state.user;
}
